import { useState } from "react";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { Lock, LogOut, Home, LayoutGrid, ChefHat, NotebookPen, SlidersHorizontal } from "lucide-react";
import { staffApi } from "./staffApi";
import StaffApp from "./StaffApp";
import AdminX from "../experience/pages/AdminX";

const HUB_KEY = "ut_hub";
const TOOLS = { admin: "admin", board: "board", kitchen: "kitchen", waiter: "waiter", veergi: "waiter", floor: "board", home: null };

const clearHub = () => {
  localStorage.removeItem(HUB_KEY);
  ["xp_admin_ok", "xp_admin_role", "xp_admin_token", "xp_admin_kicked", "ut_staff_token", "ut_staff"]
    .forEach((k) => sessionStorage.removeItem(k));
};

const hydrate = (h) => {
  if (h.admin_token) {
    sessionStorage.setItem("xp_admin_ok", "1");
    sessionStorage.setItem("xp_admin_role", h.role);
    sessionStorage.setItem("xp_admin_token", h.admin_token);
  }
  if (h.staff?.token && !sessionStorage.getItem("ut_staff_token")) {
    sessionStorage.setItem("ut_staff_token", h.staff.token);
    sessionStorage.setItem("ut_staff", JSON.stringify({ staff_id: h.staff.staff_id, name: h.staff.name, role: h.staff.role }));
  }
};

const readHub = () => {
  if (sessionStorage.getItem("xp_admin_kicked")) { clearHub(); return null; }
  try {
    const h = JSON.parse(localStorage.getItem(HUB_KEY));
    if (!h?.exp || Date.now() > h.exp) { clearHub(); return null; }
    return h;
  } catch { clearHub(); return null; }
};

const Gate = ({ onUnlock }) => {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    try {
      const r = await staffApi.post("/staff-hub/unlock", { code: code.trim() });
      if (r.data.device_token) localStorage.setItem("ut_device_token", r.data.device_token);
      sessionStorage.removeItem("xp_admin_kicked");
      const h = { role: r.data.role, name: r.data.staff?.name || r.data.role,
        admin_token: r.data.admin_token || "", staff: r.data.staff || null,
        exp: Date.now() + 11.5 * 3600 * 1000 };
      localStorage.setItem(HUB_KEY, JSON.stringify(h));
      hydrate(h);
      onUnlock(h);
    } catch (e) {
      setErr(e.response?.status === 429 ? "Too many tries — wait a few minutes" : "Wrong passcode");
      setCode("");
    }
    setBusy(false);
  };
  return (
    <div data-testid="staff-hub-gate" style={{ minHeight: "100vh", background: "#FBF6EC", color: "#3E2A1A",
      fontFamily: '"Poppins", -apple-system, Helvetica, Arial, sans-serif',
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
      <style>{'@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Yeseva+One&display=swap");'}</style>
      <Lock size={30} color="#B8892B" />
      <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.35em", color: "#B8892B", margin: "16px 0 2px" }}>STAFF ONLY</p>
      <h1 style={{ fontFamily: '"Yeseva One", serif', fontSize: 32, color: "#C62828", fontWeight: 400, margin: 0 }}>Urban Turban Staff</h1>
      <p style={{ fontSize: 13, color: "#9a8f7c", margin: "8px 0 0", maxWidth: 320 }}>Enter your passcode or staff PIN. This unlock lasts 12 hours on this device only.</p>
      <input data-testid="staff-hub-passcode-input" type="password" inputMode="numeric" value={code}
        onChange={(e) => { setCode(e.target.value); setErr(""); }}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        style={{ marginTop: 22, width: 200, background: "#FFFDF8", border: "1.5px solid #E5D9BE", borderRadius: 999,
          padding: "13px 18px", textAlign: "center", fontSize: 18, letterSpacing: "0.5em", color: "#3E2A1A", outline: "none" }} />
      {err && <p data-testid="staff-hub-error" style={{ color: "#C62828", fontSize: 13, fontWeight: 700, margin: "12px 0 0" }}>{err}</p>}
      <button data-testid="staff-hub-unlock-btn" onClick={submit} disabled={busy}
        style={{ marginTop: 18, background: "#B8892B", color: "#fff", border: "none", borderRadius: 999,
          padding: "13px 38px", fontSize: 12, fontWeight: 800, letterSpacing: "0.15em", cursor: "pointer", opacity: busy ? 0.6 : 1 }}>
        {busy ? "CHECKING…" : "UNLOCK"}
      </button>
    </div>
  );
};

const Bar = ({ hub, onHome, onLogout, atHome }) => (
  <div data-testid="staff-hub-bar" style={{ position: "sticky", top: 0, zIndex: 60, height: 44, background: "#F3EBDD",
    borderBottom: "1px solid #E5D9BE", display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "0 12px", fontFamily: '"Poppins", -apple-system, Helvetica, Arial, sans-serif' }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {!atHome && (
        <button data-testid="staff-hub-home-btn" onClick={onHome}
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#FFFDF8", border: "1px solid #E5D9BE",
            borderRadius: 999, padding: "6px 14px", fontSize: 11, fontWeight: 800, color: "#3E2A1A", cursor: "pointer", letterSpacing: "0.08em" }}>
          <Home size={13} /> STAFF HOME
        </button>
      )}
      <span data-testid="staff-hub-role" style={{ fontSize: 11, fontWeight: 800, color: "#B8892B", letterSpacing: "0.12em", textTransform: "uppercase" }}>
        {hub.name && hub.name !== hub.role ? `${hub.name} · ${hub.role}` : hub.role}
      </span>
    </div>
    <button data-testid="staff-logout-btn" onClick={onLogout}
      style={{ display: "flex", alignItems: "center", gap: 6, background: "#C62828", color: "#fff", border: "none",
        borderRadius: 999, padding: "7px 16px", fontSize: 11, fontWeight: 800, cursor: "pointer", letterSpacing: "0.1em" }}>
      <LogOut size={13} /> LOG OUT
    </button>
  </div>
);

const Tile = ({ testId, icon: Icon, title, sub, onClick }) => (
  <button data-testid={testId} onClick={onClick}
    style={{ background: "#FFFDF8", border: "1.5px solid #E5D9BE", borderRadius: 18, padding: "26px 20px",
      textAlign: "left", cursor: "pointer", boxShadow: "0 1px 6px rgba(62,42,26,0.06)", fontFamily: "inherit" }}>
    <Icon size={26} color="#B8892B" />
    <p style={{ fontSize: 18, fontWeight: 800, color: "#3E2A1A", margin: "12px 0 2px" }}>{title}</p>
    <p style={{ fontSize: 12, color: "#9a8f7c", margin: 0 }}>{sub}</p>
  </button>
);

const HubHome = ({ hub, go, canAdmin }) => (
  <div data-testid="staff-hub-home" style={{ minHeight: "calc(100vh - 44px)", background: "#FBF6EC", color: "#3E2A1A",
    fontFamily: '"Poppins", -apple-system, Helvetica, Arial, sans-serif', padding: "30px 18px 60px" }}>
    <style>{'@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Yeseva+One&display=swap");'}</style>
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      <h1 style={{ fontFamily: '"Yeseva One", serif', fontSize: 34, color: "#C62828", fontWeight: 400, margin: 0 }}>Staff tools</h1>
      <p style={{ fontSize: 13, color: "#9a8f7c", margin: "6px 0 22px" }}>Pick a tool. Customer ordering stays separate at /t/table.</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
        {canAdmin && (
          <Tile testId="hub-tile-admin" icon={SlidersHorizontal} title="Admin"
            sub={hub.role === "owner" ? "Tables, orders, menu, rewards, QR, Settings" : "Tables, orders, menu, rewards, QR"}
            onClick={() => go("admin")} />
        )}
        <Tile testId="hub-tile-waiter" icon={NotebookPen} title="VeerGi · Waiter" sub="Shift sign-in and waiter order flow" onClick={() => go("waiter")} />
        <Tile testId="hub-tile-board" icon={LayoutGrid} title="Board" sub="Live tables and order board" onClick={() => go("board")} />
        <Tile testId="hub-tile-kitchen" icon={ChefHat} title="Kitchen" sub="Kitchen screen — tickets and 86 list" onClick={() => go("kitchen")} />
      </div>
    </div>
  </div>
);

export default function StaffHub() {
  const { tool } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(() => { const h = readHub(); if (h) hydrate(h); return h; });
  if (!hub) return <Gate onUnlock={setHub} />;
  if (tool && TOOLS[tool] === undefined) return <Navigate to="/staff" replace />;
  const t = tool ? TOOLS[tool] : null;
  const canAdmin = hub.role === "owner" || hub.role === "manager";
  if (t === "admin" && !canAdmin) return <Navigate to="/staff" replace />;
  const logout = () => { clearHub(); setHub(null); navigate("/staff"); };
  return (
    <div>
      <Bar hub={hub} atHome={!t} onHome={() => navigate("/staff")} onLogout={logout} />
      {!t && <HubHome hub={hub} canAdmin={canAdmin} go={(k) => navigate(`/staff/${k}`)} />}
      {t === "admin" && (
        <div style={{ background: "#0B0B0D", minHeight: "calc(100vh - 44px)" }} className="text-white">
          <AdminX />
        </div>
      )}
      {(t === "board" || t === "kitchen" || t === "waiter") && (
        <StaffApp key={t} initialView={t === "waiter" ? "home" : t} embedded />
      )}
    </div>
  );
}
